// IndexedDB persistence for the companion tracker. Two jobs: (1) buffer live
// points during a session so tunnels, dead zones and accidental refreshes
// lose nothing, and (2) keep finished sessions on the device. Sessions are
// private by default and only leave the phone via the explicit opt-in sync.
// Plain functions, no React, no framework imports; dynamic import safe.

import type { Segment, SessionStats, TrackingSport, TrailPoint } from "@/lib/tracking/types";
import type { AbilityLevel } from "@/lib/planner/skill-fit";
import { openCompanionDb as openDb } from "./device-database";
import { assertOfflineContext, offlineContext } from "@/lib/offline/context";
import { getLibraryContext, type LibraryContext } from "@/native-shell/library";
import { companionSessionCapture, mergeRecordingCapture, recordingCapture, type RecordingCapture } from "./session-provenance";
import { preserveActivityChoice, validActivityChoice, type ActivityChoice } from "./session-activity";
import { GPS_EVIDENCE_VERSION, GPS_EVIDENCE_MAX_POINTS, buildRecordingEvidence, preserveEvidenceVersion, unavailableRecordingEvidence, type RecordingEvidence, type RecordingEvidenceSummary } from "./recording-evidence";

export class RecordingOwnerError extends Error {
  constructor() { super("A recording is protected for a different account. Sign in to that account to recover it."); }
}

export type StoredSessionStats = SessionStats & { activityChoice?: ActivityChoice };
export type StoredTrailPoint = TrailPoint;
export type StoredSegment = Segment;

export type SessionRecord = {
  evidenceSummary?: RecordingEvidenceSummary;
  offlineOwner?: string;
  outingId?: string;
  outingLinked?: boolean;
  id: string;
  sport: TrackingSport;
  startedAt: number; // epoch ms
  endedAt: number;
  stats: StoredSessionStats;
  trail: StoredTrailPoint[];
  segments: StoredSegment[];
  /** Location label the user saw when starting (resort/spot), if any. */
  placeLabel: string | null;
  placeSlug: string | null;
  consentVersion?: string | null;
  /** Optional ability level known at tracking time, used for skill-stamped story cards. */
  abilityLevel?: AbilityLevel | null;
  synced: boolean;
};

export type LiveBufferMeta = {
  gpsEvidenceVersion?: typeof GPS_EVIDENCE_VERSION;
  capture?: RecordingCapture;
  activityChoice?: ActivityChoice;
  /** New buffers can be recognised safely by the independent offline home. */
  offlineOwner?: string;
  outingId?: string;
  sessionId: string;
  sport: TrackingSport;
  startedAt: number;
  placeLabel: string | null;
  placeSlug: string | null;
};

export type LivePoint = {
  t: number;
  lat: number;
  lng: number;
  ele: number | null;
  accuracy: number | null;
  speedMs: number | null;
};

export type RecordingIdentity = { sessionId: string; offlineOwner: string };
const ownerOf = (record: { offlineOwner?: string }) => record.offlineOwner ?? "device";
function owns(record: { offlineOwner?: string }, context: LibraryContext) { return ownerOf(record) === context.owner; }
function requireJournal(meta: LiveBufferMeta | undefined, identity: RecordingIdentity, context: LibraryContext) {
  if (!meta || !owns(meta, context) || identity.offlineOwner !== context.owner || meta.sessionId !== identity.sessionId) {
    throw new RecordingOwnerError();
  }
}

const DB_NAME = "pw-companion";
const SESSIONS = "sessions";
const LIVE_META = "live-meta";
const LIVE_POINTS = "live-points";
const EVIDENCE = "session-evidence";
type StoredRecordingEvidence = { id: string; offlineOwner: string; evidence: RecordingEvidence };


function tx<T>(
  db: IDBDatabase,
  stores: string[],
  mode: IDBTransactionMode,
  run: (t: IDBTransaction) => IDBRequest<T> | void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(stores, mode);
    let result: T | undefined;
    t.oncomplete = () => resolve(result as T);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
    try {
      const req = run(t);
      if (req) req.onsuccess = () => (result = req.result);
    } catch (error) {
      t.abort();
      reject(error);
    }
  });
}

/** Never overwrite another unfinished recording, including one owned by this account. */
export async function startLiveBuffer(meta: LiveBufferMeta): Promise<void> {
  return writeLiveBuffer(meta);
}

/** Replace a recovered journal atomically, preserving the old copy if storage fails. */
export async function restoreLiveBuffer(meta: LiveBufferMeta, points: LivePoint[]): Promise<void> {
  return writeLiveBuffer(meta, points);
}

async function writeLiveBuffer(meta: LiveBufferMeta, points?: LivePoint[]): Promise<void> {
  if (meta.activityChoice !== undefined && !validActivityChoice(meta.activityChoice, meta.sport)) throw new Error("Choose a valid recording activity.");
  if (meta.gpsEvidenceVersion !== undefined && meta.gpsEvidenceVersion !== GPS_EVIDENCE_VERSION) throw new Error("GPS evidence needs a real recording source.");
  const context = await offlineContext();
  if (meta.offlineOwner && meta.offlineOwner !== context.owner) throw new RecordingOwnerError();
  const db = await openDb();
  let failure: unknown;
  try {
  await assertOfflineContext(context);
  await tx(db, [LIVE_META, LIVE_POINTS], "readwrite", (t) => {
    const previous = t.objectStore(LIVE_META).get("current");
    previous.onsuccess = () => {
      try {
        const saved = previous.result as LiveBufferMeta | undefined;
        if (!saved && meta.gpsEvidenceVersion && meta.capture !== "device-gps") throw new Error("GPS evidence needs a real recording source.");
        if (saved && (!owns(saved, context) || saved.sessionId !== meta.sessionId || saved.startedAt !== meta.startedAt || saved.sport !== meta.sport)) throw new RecordingOwnerError();
        const choice = saved ? preserveActivityChoice(saved.activityChoice, meta.activityChoice, meta.sport) : meta.activityChoice;
        const evidenceVersion = saved ? preserveEvidenceVersion(saved.gpsEvidenceVersion, meta.gpsEvidenceVersion) : meta.gpsEvidenceVersion;
        if (!points && saved) return;
        const put = (existing: LivePoint[]) => {
          try {
            // Preserve conflicting observations for evidence rejection. Choosing
            // the first fix at a timestamp would hide a broken recovery source.
            const key = (point: LivePoint) => JSON.stringify([point.t, point.lat, point.lng, point.ele, point.accuracy, point.speedMs ?? null]);
            const merged = new Map(existing.map(point => [key(point), point]));
            for (const point of points ?? []) if (!merged.has(key(point))) merged.set(key(point), point);
            const ordered = [...merged.values()].sort((a, b) => a.t - b.t);
            if (ordered.length > 300_000 || new TextEncoder().encode(JSON.stringify(ordered)).byteLength > 8 * 1024 * 1024) throw new Error("This recording exceeds the recovery storage limit. Its original copy is retained.");
            const store = t.objectStore(LIVE_POINTS);
            store.clear();
            for (const point of ordered) store.add(point);
            t.objectStore(LIVE_META).put({ ...meta, activityChoice: choice, gpsEvidenceVersion: evidenceVersion, capture: saved ? mergeRecordingCapture(saved.capture, meta.capture) : recordingCapture(meta.capture), offlineOwner: context.owner }, "current");
          } catch (error) { failure = error; t.abort(); }
        };
        if (saved) {
          const existing = t.objectStore(LIVE_POINTS).getAll();
          existing.onsuccess = () => put(existing.result as LivePoint[]);
        } else put([]);
      } catch (error) { failure = error; t.abort(); }
    };
  });
  await assertOfflineContext(context);
  } catch (error) { throw failure ?? error; } finally { db.close(); }
}

/** Append points to the live buffer (called in small batches, not per fix). */
export async function appendLivePoints(identity: RecordingIdentity, points: LivePoint[]): Promise<void> {
  if (points.length === 0) return;
  // The recorder has already initialised its account. Read the local context
  // directly so an account-change checkpoint can drain before changing owner.
  const context = await getLibraryContext();
  if (identity.offlineOwner !== context.owner) throw new RecordingOwnerError();
  const db = await openDb();
  let failure: unknown;
  try {
    const current = await getLibraryContext();
    if (current.owner !== context.owner || current.generation !== context.generation) throw new RecordingOwnerError();
    await tx(db, [LIVE_META, LIVE_POINTS], "readwrite", t => {
      const request = t.objectStore(LIVE_META).get("current");
      request.onsuccess = () => {
        try {
          requireJournal(request.result, identity, context);
          const store = t.objectStore(LIVE_POINTS);
          for (const point of points) store.add(point);
        } catch (error) { failure = error; t.abort(); }
      };
    });
  } catch (error) { throw failure ?? error; } finally { db.close(); }
}

/** The interrupted-session check: meta + buffered points, if any. */
export async function readLiveBuffer(): Promise<{ meta: LiveBufferMeta; points: LivePoint[] } | null> {
  const context = await offlineContext();
  const db = await openDb();
  try {
    let result: { meta: LiveBufferMeta; points: LivePoint[] } | null = null;
    await tx(db, [LIVE_META, LIVE_POINTS], "readonly", t => {
      const request = t.objectStore(LIVE_META).get("current");
      request.onsuccess = () => {
        const meta = request.result as LiveBufferMeta | undefined;
        if (!meta || !owns(meta, context)) return;
        const points = t.objectStore(LIVE_POINTS).getAll();
        points.onsuccess = () => { result = { meta, points: points.result as LivePoint[] }; };
      };
    });
    await assertOfflineContext(context);
    return result;
  } finally { db.close(); }
}

export async function clearLiveBuffer(identity: RecordingIdentity): Promise<void> {
  const context = await offlineContext();
  const db = await openDb();
  let failure: unknown;
  try {
    await assertOfflineContext(context);
    await tx(db, [LIVE_META, LIVE_POINTS], "readwrite", t => {
      const request = t.objectStore(LIVE_META).get("current");
      request.onsuccess = () => {
        try {
          requireJournal(request.result, identity, context);
          t.objectStore(LIVE_POINTS).clear();
          t.objectStore(LIVE_META).delete("current");
        } catch (error) { failure = error; t.abort(); }
      };
    });
  } catch (error) { throw failure ?? error; } finally { db.close(); }
}

export async function saveSession(record: SessionRecord): Promise<void> {
  const context = await offlineContext();
  if (!owns(record, context)) throw new RecordingOwnerError();
  await mutateSession(record.id, context, () => ({ ...record, offlineOwner: context.owner }));
}

/** Save the finished record and clear recovery data in one atomic transaction. */
export async function finishLiveSession(record: SessionRecord, options: { evidenceComplete?: boolean; omitEvidence?: boolean } = {}): Promise<SessionRecord> {
  const context = await offlineContext();
  if (!owns(record, context)) throw new RecordingOwnerError();
  const db = await openDb();
  let savedRecord: SessionRecord | undefined;
  let failure: unknown;
  try {
    await assertOfflineContext(context);
    await tx(db, [SESSIONS, LIVE_META, LIVE_POINTS, EVIDENCE], "readwrite", (transaction) => {
      const previous = transaction.objectStore(LIVE_META).get("current");
      previous.onsuccess = () => {
        try {
          const meta = previous.result as LiveBufferMeta | undefined;
          if (meta) requireJournal(meta, { sessionId: record.id, offlineOwner: context.owner }, context);
          if (meta && recordingCapture(meta.capture) !== "device-gps" && companionSessionCapture(record.consentVersion) === "tracked") throw new Error("The recording source cannot be upgraded when saving.");
          if (meta?.capture === "simulation" && companionSessionCapture(record.consentVersion) !== "simulation") throw new Error("A demo recording must keep its source.");
          if (meta) {
            preserveActivityChoice(meta.activityChoice, record.stats.activityChoice, record.sport);
            if (meta.activityChoice && !record.stats.activityChoice) throw new Error("Keep the activity chosen when this recording started.");
          }
          const sessions = transaction.objectStore(SESSIONS), existing = sessions.get(record.id);
          existing.onsuccess = () => {
            try {
              if (existing.result && !owns(existing.result, context)) throw new RecordingOwnerError();
              if (existing.result) {
                // A completed identity is immutable here. A retry can recover
                // its acknowledgement after an account-check interruption.
                const current = existing.result as SessionRecord;
                if (meta || current.startedAt !== record.startedAt || current.endedAt !== record.endedAt || current.sport !== record.sport) throw new Error("This session was already saved.");
                savedRecord = current;
                return;
              }
              const persist = (points: LivePoint[]) => {
                try {
                  const built = options.omitEvidence ? unavailableRecordingEvidence("not-retained") : buildRecordingEvidence(meta, record, points, options.evidenceComplete === true);
                  savedRecord = { ...record, offlineOwner: context.owner, evidenceSummary: built.summary };
                  sessions.put(savedRecord);
                  if (built.evidence) transaction.objectStore(EVIDENCE).put({ id: record.id, offlineOwner: context.owner, evidence: built.evidence } satisfies StoredRecordingEvidence);
                  transaction.objectStore(LIVE_POINTS).clear();
                  transaction.objectStore(LIVE_META).delete("current");
                } catch (error) { failure = error; transaction.abort(); }
              };
              if (meta?.gpsEvidenceVersion === GPS_EVIDENCE_VERSION && !options.omitEvidence && options.evidenceComplete) {
                const points = transaction.objectStore(LIVE_POINTS).getAll(undefined, GPS_EVIDENCE_MAX_POINTS + 1);
                points.onsuccess = () => persist(points.result as LivePoint[]);
              } else persist([]);
            } catch (error) { failure = error; transaction.abort(); }
          };
        } catch (error) { failure = error; transaction.abort(); }
      };
    });
    await assertOfflineContext(context);
    if (!savedRecord) throw new Error("The session save was not acknowledged.");
    return savedRecord;
  } catch (error) {
    throw failure ?? error;
  } finally {
    db.close();
  }
}

/** Detailed fixes never travel through session-list or Trip Story reads. */
export async function readSessionEvidence(id: string): Promise<RecordingEvidence | null> {
  const context = await offlineContext(), db = await openDb();
  let evidence: RecordingEvidence | null = null;
  try {
    await assertOfflineContext(context);
    await tx(db, [SESSIONS, EVIDENCE], "readonly", transaction => {
      const session = transaction.objectStore(SESSIONS).get(id);
      session.onsuccess = () => {
        const record = session.result as SessionRecord | undefined;
        if (!record || !owns(record, context) || record.evidenceSummary?.state !== "stored") return;
        const request = transaction.objectStore(EVIDENCE).get(id);
        request.onsuccess = () => {
          const value = request.result as StoredRecordingEvidence | undefined;
          if (value && owns(value, context) && value.evidence.sessionId === record.id && value.evidence.startedAt === record.startedAt && value.evidence.endedAt === record.endedAt && value.evidence.sport === record.sport && record.consentVersion === "sensor-gps-v1" && value.evidence.activity === record.stats.activityChoice?.activity) evidence = value.evidence;
        };
      };
    });
    await assertOfflineContext(context);
    return evidence;
  } finally { db.close(); }
}

export async function listSessions(): Promise<SessionRecord[]> {
  const context = await offlineContext();
  const db = await openDb();
  try {
  const all = await tx<SessionRecord[]>(db, [SESSIONS], "readonly", (t) =>
    t.objectStore(SESSIONS).index("owner").getAll(context.owner),
  );
  await assertOfflineContext(context);
  return (all ?? []).filter(record => owns(record, context)).sort((a, b) => b.startedAt - a.startedAt);
  } finally { db.close(); }
}

/**
 * Read only explicitly allowed sessions from an already-existing device store.
 * Fixture callers use this fail-closed path so a demo page never creates the
 * Companion database or exposes unrelated private sessions.
 */
export async function listExistingSessionsByIds(
  allowedIds: readonly string[],
): Promise<SessionRecord[]> {
  const ids = [...new Set(allowedIds)];
  if (ids.length === 0 || typeof indexedDB === "undefined") return [];

  const inspectDatabases = indexedDB.databases;
  if (typeof inspectDatabases !== "function") return [];

  let db: IDBDatabase | null = null;
  try {
    const databases = await inspectDatabases.call(indexedDB);
    if (!databases.some((database) => database.name === DB_NAME)) return [];

    db = await openExistingDb();
    if (!db.objectStoreNames.contains(SESSIONS)) return [];

    const sessions = await readSessionsByIds(db, ids);
    return sessions
      .filter((session): session is SessionRecord => Boolean(session))
      .filter(session => ownerOf(session) === "device")
      .sort((a, b) => b.startedAt - a.startedAt);
  } catch {
    return [];
  } finally {
    db?.close();
  }
}

function openExistingDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME);
    req.onupgradeneeded = () => {
      req.transaction?.abort();
      req.result.close();
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("Companion database is blocked"));
  });
}

function readSessionsByIds(
  db: IDBDatabase,
  ids: readonly string[],
): Promise<(SessionRecord | undefined)[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(SESSIONS, "readonly");
    const store = transaction.objectStore(SESSIONS);
    const sessions: (SessionRecord | undefined)[] = new Array(ids.length);

    ids.forEach((id, index) => {
      const request = store.get(id) as IDBRequest<SessionRecord | undefined>;
      request.onsuccess = () => {
        sessions[index] = request.result;
      };
      request.onerror = () => reject(request.error);
    });

    transaction.oncomplete = () => resolve(sessions);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

export async function getSession(id: string): Promise<SessionRecord | null> {
  const context = await offlineContext();
  const db = await openDb();
  try {
  const rec = await tx<SessionRecord | undefined>(db, [SESSIONS], "readonly", (t) =>
    t.objectStore(SESSIONS).get(id),
  );
  await assertOfflineContext(context);
  return rec && owns(rec, context) ? rec : null;
  } finally { db.close(); }
}

async function mutateSession(id: string, context: LibraryContext, change: (record: SessionRecord | undefined) => SessionRecord | null | undefined) {
  await assertOfflineContext(context);
  const db = await openDb();
  let failure: unknown;
  try {
    await assertOfflineContext(context);
    await tx(db, [SESSIONS, EVIDENCE], "readwrite", t => {
      const store = t.objectStore(SESSIONS), request = store.get(id);
      request.onsuccess = () => {
        try {
          const current = request.result as SessionRecord | undefined;
          if (current && !owns(current, context)) throw new RecordingOwnerError();
          const next = change(current);
          if (next) {
            if (current?.evidenceSummary?.state === "stored" &&
              (next.id !== current.id || next.startedAt !== current.startedAt || next.endedAt !== current.endedAt || next.sport !== current.sport || next.consentVersion !== current.consentVersion || next.stats.activityChoice?.activity !== current.stats.activityChoice?.activity)) {
              throw new Error("A recording with saved evidence must keep its original identity and activity.");
            }
            store.put({ ...next, evidenceSummary: current?.evidenceSummary });
          }
          else if (next === null) {
            store.delete(id);
            const evidence = t.objectStore(EVIDENCE).get(id);
            evidence.onsuccess = () => {
              try {
                if (evidence.result && !owns(evidence.result as StoredRecordingEvidence, context)) throw new RecordingOwnerError();
                t.objectStore(EVIDENCE).delete(id);
              } catch (error) { failure = error; t.abort(); }
            };
          }
        } catch (error) { failure = error; t.abort(); }
      };
    });
    await assertOfflineContext(context);
  } catch (error) { throw failure ?? error; } finally { db.close(); }
}

export async function deleteSession(id: string, expected?: LibraryContext): Promise<void> {
  await mutateSession(id, expected ?? await offlineContext(), () => null);
}

export async function markSessionSynced(id: string, expected?: LibraryContext): Promise<void> {
  await mutateSession(id, expected ?? await offlineContext(), record => record ? { ...record, synced: true } : undefined);
}

/** Delete only this owner's tracking data. Other accounts and their journals survive. */
export async function deleteAllLocalData(expected?: LibraryContext): Promise<void> {
  const context = expected ?? await offlineContext();
  await assertOfflineContext(context);
  const db = await openDb();
  try {
    await assertOfflineContext(context);
    await tx(db, [SESSIONS, LIVE_META, LIVE_POINTS, EVIDENCE], "readwrite", t => {
      const cursor = t.objectStore(SESSIONS).openCursor();
      cursor.onsuccess = () => {
        const row = cursor.result;
        if (!row) return;
        if (owns(row.value as SessionRecord, context)) row.delete();
        row.continue();
      };
      const evidenceKeys = t.objectStore(EVIDENCE).index("owner").openKeyCursor(IDBKeyRange.only(context.owner));
      evidenceKeys.onsuccess = () => {
        const key = evidenceKeys.result;
        if (!key) return;
        t.objectStore(EVIDENCE).delete(key.primaryKey);
        key.continue();
      };
      const meta = t.objectStore(LIVE_META).get("current");
      meta.onsuccess = () => {
        if (meta.result && owns(meta.result as LiveBufferMeta, context)) {
          t.objectStore(LIVE_POINTS).clear();
          t.objectStore(LIVE_META).delete("current");
        }
      };
    });
    await assertOfflineContext(context);
  } finally { db.close(); }
}


/** Bounded linked-session reads never expose legacy records with an unknown owner. */
export async function listOutingSessions(outingId: string, owner: string): Promise<SessionRecord[]> {
  const before = await offlineContext();
  if (before.owner !== owner) throw new Error("Your account changed. Reopen the plan.");
  const db = await openDb();
  let rows: SessionRecord[];
  try { rows = await tx<SessionRecord[]>(db, [SESSIONS], "readonly", transaction => transaction.objectStore(SESSIONS).index("outing").getAll(outingId, 20)); }
  finally { db.close(); }
  const after = await offlineContext();
  if (before.owner !== after.owner || before.generation !== after.generation) throw new Error("Your account changed. Reopen the plan.");
  return rows.filter(row => row.offlineOwner === owner).sort((a,b) => b.startedAt - a.startedAt);
}


export async function markOutingSessionLinked(id: string, outingId: string, owner: string): Promise<void> {
  const context = await offlineContext();
  if (context.owner !== owner) return;
  await mutateSession(id, context, current => current?.offlineOwner === owner && current.outingId === outingId ? { ...current, outingLinked: true } : undefined);
}
